import React, { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Task, CreateTaskRequest, UpdateTaskRequest } from 'shared/types';
import { ImageService } from '../../services/imageService';
import { createFamilyTaskTemplateApi, AgeGroupCode, uploadFamilyPicture, rotateFamilyPicture } from '../../services/apiService';
import { UnreadableImageError } from '../../utils/prepareImage';
import { StepTimeoutError } from '../../utils/withTimeout';
import { saveInBackground } from '../../utils/saveInBackground';
import { i18n } from '../../i18n/i18n';
import { useLanguage } from '../../context/LanguageContext';
import { getToolDisplayName } from '../../utils/toolLabels';
import { showToast } from '../kid/Toast';
import { ageLabel } from './adminHelpers';
import CameraModal from '../CameraModal';
import { EditorModal, useCloseGuard } from './EditorModal';
import StepTimerInput from './StepTimerInput';
import { StoredPicture } from '../kid/StoredPicture';
import { dropIndexForY, moveItem } from '../../utils/reorder';
import RepeatSettings from './RepeatSettings';
import { nextTaskChoices, seriesOf } from '../../utils/taskChains';
import { groupMembers, withPendingEdit } from '../../utils/sharedTasks';

/**
 * Admin → Tasks: the create / edit task form (split out of TasksTab, 2026-10-12). Mounted only while
 * open, so its state starts fresh from `task` (or the defaults for a new task) each time.
 */
const NEW_TASK_DEFAULTS: Partial<Task> = {
  title: '',
  room_id: null,
  points: 5,
  due_date: undefined,
  recurrence_days: 0,
  recreate_on_validation: false,
  days_to_complete: undefined,
  auto_available: false,
  header_image: undefined,
  assigned_to: undefined,
  tools: [],
} as any;

const TaskFormModal: React.FC<{ task: Task | null; isCreating: boolean; onClose: () => void }> = ({ task: shownTask, isCreating, onClose }) => {
  const { current_family, createTask, updateTask } = useAppContext() as any;
  // A shared task's edit that is still waiting for someone's next round is what the parent last saved.
  const task = shownTask ? withPendingEdit(shownTask as any) as Task : null;
  const groupId: string | null = (task as any)?.share_group_id ?? null;
  const { current_language } = useLanguage();
  // Who does it: nobody ticked = anyone can take it; two or more = a copy each (2026-10-14).
  const [assignees, setAssignees] = useState<string[]>(() => {
    if (groupId) return groupMembers(current_family?.tasks || [], groupId, (current_family?.members || []).map((m: any) => m.id));
    return (task as any)?.assigned_to ? [(task as any).assigned_to] : [];
  });
  const [tempTaskData, setTempTaskData] = useState<Partial<Task>>(() =>
    // a shared task is for several people, so no single assigned_to
    isCreating || !task ? { ...NEW_TASK_DEFAULTS } : { ...task, assigned_to: groupId ? undefined : (task as any).assigned_to });
  const setWho = (next: string[]) => {
    setAssignees(next);
    setTempTaskData((prev) => ({ ...prev, assigned_to: next.length === 1 ? next[0] : undefined } as any));
  };
  const [tempTaskHeaderImageURL, setTempTaskHeaderImageURL] = useState<string | null>(() => (task as any)?.header_image_url || null);
  const [uploadingPictures, setUploadingPictures] = useState(0);
  const [editingChecklist, setEditingChecklist] = useState<Array<{ id: string; text: string; images?: string[]; tool_ids?: string[]; timer_seconds?: number | null }>>(() =>
    isCreating || !task ? [] : (task.checklist || []) as any);
  const [editingTools, setEditingTools] = useState<string[]>(() => (isCreating || !task ? [] : task.tools || []));
  const [editingChecklistPreviewURLs, setEditingChecklistPreviewURLs] = useState<Record<string, string[]>>({});
  const [taskCameraModal, setTaskCameraModal] = useState<{ open: boolean; target: { type: 'header' } | { type: 'checklist'; itemId: string } }>({ open: false, target: { type: 'header' } });
  const [taskTempImageIds, setTaskTempImageIds] = useState<string[]>([]);
  const getToolLabel = (tool: any): string => getToolDisplayName(tool, current_language);

  const handleCreateTask = async () => {
    try {
      if (!tempTaskData.title || !tempTaskData.points || tempTaskData.points <= 0) {
        showToast({ message: i18n.t('admin_panel.err_task_title_points'), tone: 'error' });
        return;
      }

      // Filter out empty checklist items
      const validChecklistItems = editingChecklist.filter(item => item.text && item.text.trim() !== '');
      
      if (validChecklistItems.length === 0) {
        showToast({ message: i18n.t('admin_panel.err_checklist_empty'), tone: 'error' });
        return;
      }

      const newTask: CreateTaskRequest = {
        title: tempTaskData.title,
        description: tempTaskData.description,
        room_id: tempTaskData.room_id || undefined,
        points: tempTaskData.points,
        header_image: tempTaskData.header_image,
        header_image_url: (tempTaskData as any).header_image_url ?? null,
        checklist: validChecklistItems.map((item, index) => ({ 
          text: item.text.trim(), 
          order_index: index + 1,
          images: item.images || [],
          // only tools the task itself still uses
          tool_ids: (item.tool_ids || []).filter((id) => editingTools.includes(id)),
          timer_seconds: item.timer_seconds ?? null,
        })),
        tools: editingTools,
        due_date: tempTaskData.due_date ? tempTaskData.due_date.toISOString() : undefined,
        recurrence_days: tempTaskData.recurrence_days,
        recreate_on_validation: (tempTaskData as any).recreate_on_validation || false,
        days_to_complete: (tempTaskData as any).days_to_complete || undefined,
        auto_available: tempTaskData.auto_available,
        next_series_id: (tempTaskData as any).next_series_id ?? null,
        next_wait_hours: (tempTaskData as any).next_wait_hours ?? null,
        repeat_schedule: (tempTaskData as any).repeat_schedule ?? null,
        counts_for_pocket_money: (tempTaskData as any).counts_for_pocket_money !== false,
        assigned_to: assignees.length === 1 ? assignees[0] : undefined,
        ...(assignees.length >= 2 ? { assignees } : {}),
        age_group: (tempTaskData as any).age_group || 'all_ages'
      };

      // Close at once; "Saving task…" until it is saved (createTask already reloads the family).
      onClose();
      void saveInBackground({
        run: () => createTask(newTask),
        saving: i18n.t('admin_panel.task_saving'),
        saved: i18n.t('admin_panel.task_saved'),
        failed: i18n.t('admin_panel.err_task_create'),
        tryAgain: i18n.t('admin_panel.try_again'),
      });
    } catch (error) {
      console.error('Failed to create task:', error);
      showToast({ message: i18n.t('admin_panel.err_task_create'), tone: 'error' });
    }
  };

  const handleUpdateTask = async (taskId: string) => {
    try {
      if (!tempTaskData.title || !tempTaskData.points || tempTaskData.points <= 0) {
        showToast({ message: i18n.t('admin_panel.err_task_title_points'), tone: 'error' });
        return;
      }

      // Filter out empty checklist items
      const validChecklistItems = editingChecklist.filter(item => item.text && item.text.trim() !== '');
      
      if (validChecklistItems.length === 0) {
        showToast({ message: i18n.t('admin_panel.err_checklist_empty'), tone: 'error' });
        return;
      }

      const taskUpdates: UpdateTaskRequest = {
        title: tempTaskData.title,
        description: tempTaskData.description,
        room_id: tempTaskData.room_id || undefined,
        points: tempTaskData.points,
        header_image: tempTaskData.header_image,
        header_image_url: (tempTaskData as any).header_image_url ?? null,
        checklist: validChecklistItems.map((item, index) => ({ 
          text: item.text.trim(), 
          order_index: index + 1,
          images: item.images || [],
          // only tools the task itself still uses
          tool_ids: (item.tool_ids || []).filter((id) => editingTools.includes(id)),
          timer_seconds: item.timer_seconds ?? null,
        })),
        tools: editingTools,
        // null clears a deadline the parent removed
        due_date: tempTaskData.due_date ? tempTaskData.due_date.toISOString() : (null as any),
        recurrence_days: tempTaskData.recurrence_days,
        recreate_on_validation: (tempTaskData as any).recreate_on_validation,
        days_to_complete: (tempTaskData as any).days_to_complete,
        auto_available: tempTaskData.auto_available,
        next_series_id: (tempTaskData as any).next_series_id ?? null,
        next_wait_hours: (tempTaskData as any).next_wait_hours ?? null,
        repeat_schedule: (tempTaskData as any).repeat_schedule ?? null,
        counts_for_pocket_money: (tempTaskData as any).counts_for_pocket_money !== false,
        assigned_to: assignees.length === 1 ? assignees[0] : undefined,
        // A shared task (or one that becomes shared) is saved for everyone it is for.
        ...(groupId || assignees.length >= 2 ? { assignees } : {}),
      };
      if (groupId && assignees.length === 0) {
        showToast({ message: i18n.t('admin_panel.err_choose_person'), tone: 'error' });
        return;
      }

      // Close at once; "Saving task…" until it is saved (updateTask already reloads the family —
      // this used to reload it a second time, which made saving feel stuck).
      onClose();
      void saveInBackground({
        run: () => updateTask(taskId, taskUpdates),
        saving: i18n.t('admin_panel.task_saving'),
        saved: i18n.t('admin_panel.task_saved'),
        failed: i18n.t('admin_panel.err_task_update'),
        tryAgain: i18n.t('admin_panel.try_again'),
      });
    } catch (error) {
      console.error('Failed to update task:', error);
      showToast({ message: i18n.t('admin_panel.err_task_update'), tone: 'error' });
    }
  };


  const handleAddChecklistItem = () => {
    const newItem = { id: Date.now().toString(), text: '' };
    setEditingChecklist([...editingChecklist, newItem]);
  };

  // Re-order steps (2026-10-13): drag the handle (mouse or touch), or use the ↑ ↓ buttons.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const checklistRef = useRef(editingChecklist);
  checklistRef.current = editingChecklist;

  const moveStep = (from: number, to: number) => setEditingChecklist(moveItem(editingChecklist, from, to));

  useEffect(() => {
    if (!draggingId) return;
    // The modal's own scroll area, so dragging near its edge scrolls a long list.
    let scroller: HTMLElement | null = rowRefs.current[draggingId]?.parentElement || null;
    while (scroller && !(scroller.scrollHeight > scroller.clientHeight && /auto|scroll/.test(getComputedStyle(scroller).overflowY))) {
      scroller = scroller.parentElement;
    }
    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const list = checklistRef.current;
      const from = list.findIndex((it) => it.id === draggingId);
      if (from < 0) return;
      const rows = list.map((it) => {
        const b = rowRefs.current[it.id]?.getBoundingClientRect();
        return { top: b?.top ?? 0, bottom: b?.bottom ?? 0 };
      });
      const to = dropIndexForY(rows, ev.clientY);
      if (to !== from) setEditingChecklist(moveItem(list, from, to));
      if (scroller) {
        const box = scroller.getBoundingClientRect();
        if (ev.clientY < box.top + 60) scroller.scrollBy(0, -14);
        else if (ev.clientY > box.bottom - 60) scroller.scrollBy(0, 14);
      }
    };
    const onUp = () => setDraggingId(null);
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [draggingId]);

  // Tools per step (2026-10-13): pick from the task's tools; the child sees them under the step.
  const toggleStepTool = (itemId: string, toolId: string) => {
    setEditingChecklist(editingChecklist.map((item) => {
      if (item.id !== itemId) return item;
      const ids = item.tool_ids || [];
      return { ...item, tool_ids: ids.includes(toolId) ? ids.filter((x) => x !== toolId) : [...ids, toolId] };
    }));
  };

  const handleUpdateChecklistItem = (id: string, text: string) => {
    setEditingChecklist(editingChecklist.map(item => 
      item.id === id ? { ...item, text } : item
    ));
  };

  const handleRemoveChecklistItem = (id: string) => {
    setEditingChecklist(editingChecklist.filter(item => item.id !== id));
  };

  // Pictures (2026-10-13): prepared (HEIC → JPEG, shrunk) and uploaded straight away, so every
  // family device sees them. They used to stay in this browser only.
  const pictureError = (error: unknown) => {
    console.error('Picture failed', error);
    showToast({
      message: error instanceof UnreadableImageError
        ? i18n.t('pictures.unreadable')
        // Which step hung (prepare/sign/upload), so a report from a phone tells us where to look.
        : i18n.t('admin_panel.err_header_image') + (error instanceof StepTimeoutError ? ` (${error.step})` : ''),
      tone: 'error',
    });
  };

  const addHeaderPicture = async (file: Blob) => {
    setUploadingPictures((n) => n + 1);
    try {
      const url = await uploadFamilyPicture('task_header', file, { task_id: task?.id });
      setTempTaskData((prev) => ({ ...prev, header_image_url: url, header_image: undefined } as any));
      setTempTaskHeaderImageURL(url);
    } catch (error) {
      pictureError(error);
    } finally {
      setUploadingPictures((n) => n - 1);
    }
  };

  // ↻ a quarter turn clockwise; the turned copy replaces the picture (2026-10-13).
  const rotateHeader = async () => {
    const current = (tempTaskData as any).header_image_url || tempTaskData.header_image;
    if (!current) return;
    setUploadingPictures((n) => n + 1);
    try {
      const url = await rotateFamilyPicture('task_header', current, { task_id: task?.id });
      setTempTaskData((prev) => ({ ...prev, header_image_url: url, header_image: undefined } as any));
      setTempTaskHeaderImageURL(url);
    } catch (error) {
      pictureError(error);
    } finally {
      setUploadingPictures((n) => n - 1);
    }
  };

  const rotateStepPicture = async (itemId: string, index: number) => {
    const current = editingChecklist.find((it) => it.id === itemId)?.images?.[index];
    if (!current) return;
    setUploadingPictures((n) => n + 1);
    try {
      const url = await rotateFamilyPicture('checklist_image', current, { task_id: task?.id, item_id: /^[0-9a-f-]{36}$/.test(itemId) ? itemId : undefined });
      setEditingChecklist((prev) => prev.map((it) => it.id === itemId
        ? { ...it, images: (it.images || []).map((im, i) => (i === index ? url : im)) }
        : it));
      setEditingChecklistPreviewURLs((prev) => {
        const list = [...(prev[itemId] || [])];
        if (list.length > index) list[index] = url;
        return { ...prev, [itemId]: list };
      });
    } catch (error) {
      pictureError(error);
    } finally {
      setUploadingPictures((n) => n - 1);
    }
  };

  const addStepPictures = async (itemId: string, files: Blob[]) => {
    for (const f of files) {
      setUploadingPictures((n) => n + 1);
      try {
        const url = await uploadFamilyPicture('checklist_image', f, { task_id: task?.id, item_id: /^[0-9a-f-]{36}$/.test(itemId) ? itemId : undefined });
        setEditingChecklist((prev) => prev.map((item) => item.id === itemId ? { ...item, images: [...(item.images || []), url] } : item));
      } catch (error) {
        pictureError(error);
      } finally {
        setUploadingPictures((n) => n - 1);
      }
    }
  };

  const handleRemoveChecklistImage = (itemId: string, index: number) => {
    setEditingChecklist(editingChecklist.map(item => 
      item.id === itemId 
        ? { ...item, images: (item.images || []).filter((_, imgIndex) => imgIndex !== index) }
        : item
    ));
  };


  // Also from an existing task (2026-10-17, Jens): in case it wasn't saved as a template when created.
  // Uses what the form shows now.
  const saveAsTemplate = async () => {
    try {
      if (!tempTaskData.title || !tempTaskData.points) {
        showToast({ message: i18n.t('admin_panel.err_task_title_points'), tone: 'error' });
        return;
      }
      const validChecklistItems = editingChecklist.filter(item => item.text && item.text.trim() !== '');
      const payload = {
        language_code: current_language,
        title: String(tempTaskData.title),
        description: tempTaskData.description || undefined,
        room_id: tempTaskData.room_id || undefined,
        points: Number(tempTaskData.points),
        recurrence_days: tempTaskData.recurrence_days || undefined,
        // An uploaded header picture goes along (the picture cleanup also checks templates);
        // an old device-only picture id can't.
        header_image_url: /^https?:/i.test(String((tempTaskData as any).header_image_url || '')) ? (tempTaskData as any).header_image_url : undefined,
        checklist_items: validChecklistItems.map((item, index) => ({ text: item.text.trim(), order_index: index + 1, images: item.images || [], ...(item.timer_seconds ? { timer_seconds: item.timer_seconds } : {}) })),
        age_group: ((tempTaskData as any).age_group as AgeGroupCode) || 'all_ages'
      };
      await createFamilyTaskTemplateApi(payload);
      showToast({ message: i18n.t('admin_panel.saved_template') });
    } catch (e) {
      console.error('Failed to save template', e);
      showToast({ message: i18n.t('admin_panel.err_save_template'), tone: 'error' });
    }
  };

  // Cancel / "Don't save": drop pictures kept only on this device during this edit, then close.
  const discardAndClose = async () => {
    if (tempTaskHeaderImageURL) {
      URL.revokeObjectURL(tempTaskHeaderImageURL);
      setTempTaskHeaderImageURL(null);
    }
    try {
      for (const id of taskTempImageIds) {
        try { await ImageService.deleteImage(id); } catch {}
      }
    } finally {
      setTaskTempImageIds([]);
    }
    onClose();
  };

  // ✕ / Esc / tap outside (2026-10-17): asks to save only when something was changed.
  const closeGuard = useCloseGuard({
    open: true,
    snapshot: { tempTaskData, assignees, editingChecklist, editingTools },
    save: () => (isCreating ? handleCreateTask() : handleUpdateTask(task!.id)),
    discard: discardAndClose,
    saveDisabled: uploadingPictures > 0,
  });

  return (
        <EditorModal
          width="4xl"
          onRequestClose={closeGuard.requestClose}
          title={isCreating ? i18n.t('admin_panel.task_modal_title_create') : `${i18n.t('admin_panel.task_modal_title_edit_prefix')}: ${task?.title}`}
        >
            <div className="p-6 space-y-6">
              <div className="space-y-6">
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.task_title_label')}</label>
                  <input 
                    type="text" 
                    placeholder={i18n.t('admin_panel.task_title_placeholder')}
                    value={tempTaskData.title || ''}
                    onChange={(e) => setTempTaskData({ ...tempTaskData, title: e.target.value })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                  />
                </div>
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.description_label')}</label>
                  <textarea 
                    placeholder={i18n.t('admin_panel.description_placeholder')}
                    value={tempTaskData.description || ''}
                    onChange={(e) => setTempTaskData({ ...tempTaskData, description: e.target.value })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                    rows={3}
                  />
                </div>
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.room_label')}</label>
                  <select 
                                    value={tempTaskData.room_id || ''}
                onChange={(e) => setTempTaskData({ ...tempTaskData, room_id: e.target.value === '' ? null : e.target.value })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                  >
                    <option value="">{i18n.t('admin_panel.no_room_option')}</option>
                    {current_family.rooms.map((room: any) => (<option key={room.id} value={room.id}>{room.name}</option>))}
                  </select>
                </div>
                
                <div className="w-full max-w-md">
                  <fieldset>
                    <legend className="block text-base font-bold mb-1">{i18n.t('admin_panel.assignment_label')}</legend>
                    {!groupId && (
                      <label className="flex items-center gap-3 min-h-[44px] text-base cursor-pointer">
                        <input type="radio" className="w-6 h-6 accent-kid-green" checked={assignees.length === 0} onChange={() => setWho([])} />
                        {i18n.t('admin_panel.available_anyone_option')}
                      </label>
                    )}
                    {current_family.members.map((member: any) => (
                      <label key={member.id} className="flex items-center gap-3 min-h-[44px] text-base cursor-pointer">
                        <input type="checkbox" className="w-6 h-6 accent-kid-green" checked={assignees.includes(member.id)}
                          onChange={(e) => setWho(e.target.checked ? [...assignees, member.id] : assignees.filter((id) => id !== member.id))} />
                        {member.name}
                      </label>
                    ))}
                  </fieldset>
                  <p className="text-xs text-kid-text-muted mt-1">
                    {assignees.length >= 2 ? i18n.t('admin_panel.assignment_hint_each')
                      : assignees.length === 1 ? i18n.t('admin_panel.assignment_hint_assigned')
                      : i18n.t('admin_panel.assignment_hint_anyone')}
                  </p>
                </div>
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.points_label')}</label>
                  <input 
                    type="number" 
                    placeholder={i18n.t('admin_panel.points_placeholder')} 
                    value={tempTaskData.points || 0}
                    onChange={(e) => setTempTaskData({ ...tempTaskData, points: parseInt(e.target.value) || 0 })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                    min="1" 
                  />
                </div>

                {/* Age Group */}
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('library.age_group')}</label>
                  <select
                    value={((tempTaskData as any).age_group as AgeGroupCode) || 'all_ages'}
                    onChange={(e) => setTempTaskData({ ...tempTaskData, age_group: e.target.value as AgeGroupCode })}
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                  >
                    <option value="all_ages">{ageLabel('all_ages')}</option>
                    <option value="age_2_3">{ageLabel('age_2_3')}</option>
                    <option value="age_4_5">{ageLabel('age_4_5')}</option>
                    <option value="age_6_7">{ageLabel('age_6_7')}</option>
                    <option value="age_8_9">{ageLabel('age_8_9')}</option>
                    <option value="age_10_12">{ageLabel('age_10_12')}</option>
                    <option value="age_13_plus">{ageLabel('age_13_plus')}</option>
                    <option value="adults">{ageLabel('adults')}</option>
                  </select>
                </div>
                
                {current_family.pocket_money_enabled && (
                  // "Gives pocket money" (2026-10-14): untick everyday tasks so only bigger jobs count.
                  <div className="w-full max-w-md">
                    <label className="flex items-center gap-3 min-h-[44px] text-base font-bold cursor-pointer">
                      <input type="checkbox" className="w-6 h-6 accent-kid-green"
                        checked={(tempTaskData as any).counts_for_pocket_money !== false}
                        onChange={(e) => setTempTaskData({ ...tempTaskData, counts_for_pocket_money: e.target.checked } as any)} />
                      {i18n.t('pocket.task_counts')}
                    </label>
                    <p className="text-xs text-kid-text-muted mt-1">{i18n.t('pocket.task_counts_help')}</p>
                  </div>
                )}

                <RepeatSettings data={tempTaskData as any} members={current_family.members}
                  assigneeNames={assignees.map((id) => current_family.members.find((m: any) => m.id === id)?.name).filter(Boolean)}
                  nextChoices={nextTaskChoices(current_family.tasks || [], task ? seriesOf(task as any) : null)} onChange={(patch) => setTempTaskData({ ...tempTaskData, ...patch } as any)} />

                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.header_image_label')}</label>
                  {tempTaskHeaderImageURL && (
                    <div className="w-full mb-2">
                      <img src={tempTaskHeaderImageURL} alt="" className="w-full h-48 object-cover rounded-kid-tile border-2 border-kid-border dark:border-kid-border-dark" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) await addHeaderPicture(f);
                      }} 
                      className="flex-1 min-w-0 min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                    />
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                      onClick={() => setTaskCameraModal({ open: true, target: { type: 'header' } })}
                    >
                      {i18n.t('common.take_photo')}
                    </button>
                  </div>
                  {(tempTaskData.header_image || (tempTaskData as any).header_image_url) && (
                    <div className="header-image-preview flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        onClick={rotateHeader}
                        disabled={uploadingPictures > 0}
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base border-2 border-kid-border-strong dark:border-kid-border-strong-dark disabled:opacity-50"
                      >
                        <span aria-hidden>↻</span> {i18n.t('pictures.rotate')}
                      </button>
                      <button 
                        onClick={() => {
                          if (tempTaskHeaderImageURL) {
                            URL.revokeObjectURL(tempTaskHeaderImageURL);
                            setTempTaskHeaderImageURL(null);
                          }
                          setTempTaskData({ ...tempTaskData, header_image: undefined, header_image_url: null } as any);
                        }} 
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50"
                      >
                        {i18n.t('pictures.remove_picture')}
                      </button>
                    </div>
                  )}
                </div>

                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.checklist_label')}</label>
                  <div className="space-y-4">
                    {editingChecklist.map((item, index) => (
                      <div
                        key={item.id}
                        ref={(el) => { rowRefs.current[item.id] = el; }}
                        className={`border-2 rounded-kid-tile p-4 transition-shadow ${draggingId === item.id
                          ? 'border-kid-green shadow-lg bg-kid-green-bg dark:bg-green-900/30 relative z-10'
                          : 'border-kid-border dark:border-kid-border-dark'}`}
                      >
                        <div className="flex gap-2 mb-3 items-start">
                          <div className="flex flex-col items-center gap-1 shrink-0">
                            <button
                              type="button"
                              aria-label={i18n.t('admin_panel.step_drag')}
                              title={i18n.t('admin_panel.step_drag')}
                              onPointerDown={(e) => { e.preventDefault(); setDraggingId(item.id); }}
                              className="w-10 h-10 rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark flex items-center justify-center text-xl cursor-grab active:cursor-grabbing select-none"
                              style={{ touchAction: 'none' }}
                            >
                              <span aria-hidden>⠿</span>
                            </button>
                            <span className="text-kid-text-muted dark:text-kid-text-muted-dark text-sm font-bold">{index + 1}.</span>
                            <button type="button" disabled={index === 0} onClick={() => moveStep(index, index - 1)}
                              aria-label={i18n.t('admin_panel.step_move_up')} title={i18n.t('admin_panel.step_move_up')}
                              className="w-10 h-8 rounded-full border-2 border-kid-border dark:border-kid-border-dark font-bold disabled:opacity-30">↑</button>
                            <button type="button" disabled={index === editingChecklist.length - 1} onClick={() => moveStep(index, index + 1)}
                              aria-label={i18n.t('admin_panel.step_move_down')} title={i18n.t('admin_panel.step_move_down')}
                              className="w-10 h-8 rounded-full border-2 border-kid-border dark:border-kid-border-dark font-bold disabled:opacity-30">↓</button>
                          </div>
                          <div className="flex-1">
                            <textarea
                              value={item.text}
                              onChange={(e) => handleUpdateChecklistItem(item.id, e.target.value)}
                              placeholder={i18n.t('admin_panel.checklist_item_placeholder')}
                              className="w-full min-h-20 p-2 border-2 border-kid-border-strong dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark placeholder-gray-400 dark:placeholder-gray-400 rounded-kid-tile text-sm resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        
                        {/* Image upload section */}
                        <div className="mb-3">
                          <label className="block text-base font-bold mb-1">
                            {i18n.t('admin_panel.images_optional_label')}
                          </label>
                          
                          <div className="flex gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={async (e) => {
                                const files = Array.from(e.target.files || []);
                                e.target.value = '';
                                await addStepPictures(item.id, files);
                              }}
                              className="flex-1 min-w-0 min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                            />
                            <button
                              type="button"
                              className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                              onClick={() => setTaskCameraModal({ open: true, target: { type: 'checklist', itemId: item.id } })}
                            >
                              {i18n.t('common.take_photo')}
                            </button>
                          </div>
                          {item.images && item.images.length > 0 && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              {item.images.map((img, imgIndex) => (
                                <div key={imgIndex} className="relative">
                                  <img
                                    src={editingChecklistPreviewURLs[item.id]?.[imgIndex] || img}
                                    alt={`${index + 1}.${imgIndex + 1}`}
                                    className="w-24 h-24 object-contain bg-kid-chip dark:bg-kid-chip-dark rounded-kid-tile border-2 border-kid-border dark:border-kid-border-dark"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => rotateStepPicture(item.id, imgIndex)}
                                    disabled={uploadingPictures > 0}
                                    aria-label={i18n.t('pictures.rotate')}
                                    title={i18n.t('pictures.rotate')}
                                    className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark font-bold text-lg flex items-center justify-center disabled:opacity-50"
                                  >
                                    ↻
                                  </button>
                                  <button
                                    onClick={() => handleRemoveChecklistImage(item.id, imgIndex)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white border-none rounded-full w-5 h-5 text-xs cursor-pointer hover:bg-red-600 transition-colors"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Tools for this step: chosen from the task's tools (picked further down) */}
                        <div className="mb-3">
                          <p className="block text-base font-bold mb-1">{i18n.t('admin_panel.step_tools_label')}</p>
                          {editingTools.length === 0 ? (
                            <p className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark">{i18n.t('admin_panel.step_tools_hint')}</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {editingTools.map((toolId) => {
                                const tool = current_family.tools.find((t: any) => t.id === toolId);
                                if (!tool) return null;
                                const on = (item.tool_ids || []).includes(toolId);
                                return (
                                  <button
                                    key={toolId}
                                    type="button"
                                    aria-pressed={on}
                                    onClick={() => toggleStepTool(item.id, toolId)}
                                    className={`min-h-[40px] px-3 rounded-full border-2 text-sm font-bold ${on ? 'bg-kid-green text-white border-kid-green' : 'bg-kid-card dark:bg-kid-card-dark border-kid-border-strong dark:border-kid-border-strong-dark'}`}
                                  >
                                    {on ? '✓ ' : ''}{getToolLabel(tool)}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Timed step (2026-10-19) */}
                        <StepTimerInput
                          seconds={item.timer_seconds}
                          onChange={(sec) => setEditingChecklist((prev) => prev.map((it) => it.id === item.id ? { ...it, timer_seconds: sec } : it))}
                        />

                        {/* Delete button moved below image upload */}
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleRemoveChecklistItem(item.id)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                          >
                            {i18n.t('common.remove_item') || 'Remove Item'}
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      onClick={handleAddChecklistItem}
                      className="w-full p-3 border-2 border-dashed border-kid-border-strong rounded-kid-tile text-kid-text-muted hover:border-gray-400 hover:text-gray-600 transition-colors"
                    >
                      + {i18n.t('admin_panel.add_checklist_item_btn') || 'Add Checklist Item'}
                    </button>
                  </div>
                </div>

                {/* Tools Selection */}
                <div className="w-full max-w-md">
                  <label className="block text-sm font-medium text-kid-text mb-2">{i18n.t('admin_panel.required_tools_label')}</label>
                  <div className="space-y-2">
                    {current_family.tools.length === 0 ? (
                      <p className="text-kid-text-muted text-sm italic">{i18n.t('admin_panel.no_tools_available_msg')}</p>
                    ) : (
                      current_family.tools.map((tool: any) => (
                        <label key={tool.id} className="flex items-center gap-3 cursor-pointer min-h-[44px]">
                          <input
                            type="checkbox"
                            checked={editingTools.includes(tool.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditingTools([...editingTools, tool.id]);
                              } else {
                                setEditingTools(editingTools.filter(id => id !== tool.id));
                              }
                            }}
                            className="w-6 h-6 accent-kid-green"
                          />
                          {/* small picture of the tool (2026-10-13) */}
                          <StoredPicture url={tool.image_url} imageId={tool.image} label={getToolLabel(tool)} size={40} rounded="rounded-kid-tile" />
                          <span className="text-kid-text">{getToolLabel(tool)}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
              
              {/* Task Camera Modal */}
              <CameraModal
                isOpen={taskCameraModal.open}
                facingMode="environment"
                title={i18n.t('admin_panel.camera_title') || 'Camera'}
                onClose={() => setTaskCameraModal(prev => ({ ...prev, open: false }))}
                onCapture={async (blob) => {
                  const target = taskCameraModal.target;
                  setTaskCameraModal(prev => ({ ...prev, open: false }));
                  if (target.type === 'header') await addHeaderPicture(blob);
                  else if (target.type === 'checklist') await addStepPictures(target.itemId, [blob]);
                }}
              />

              {uploadingPictures > 0 && (
                <p role="status" className="pt-3 text-base font-bold text-kid-text-muted">⏳ {i18n.t('pictures.preparing')}</p>
              )}
              <div className="flex flex-wrap gap-3 pt-5 border-t-2 border-kid-border dark:border-kid-border-dark">
                <button onClick={discardAndClose} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1">{i18n.t('admin_panel.cancel_btn')}</button>
                {isCreating ? (
                  <button onClick={handleCreateTask} disabled={uploadingPictures > 0} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">{i18n.t('admin_panel.create_task_btn')}</button>
                ) : (
                  <button onClick={() => handleUpdateTask(task!.id)} disabled={uploadingPictures > 0} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">{i18n.t('admin_panel.save_changes_btn')}</button>
                )}
                <button
                  onClick={saveAsTemplate}
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1 basis-full sm:basis-0"
                >
                  {i18n.t('library.save_as_template')}
                </button>
              </div>
            </div>
          {closeGuard.dialog}
        </EditorModal>
  );
};

export default TaskFormModal;
